import { gql, useReactiveVar, useSubscription } from '@apollo/client'
import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { credentialsVar } from '../../cache/auth'
import { getTranslate } from '../../localization'

export const OTHER_SESSION_ESTABLISHED = gql`
  subscription OtherSessionEstablished {
    otherSessionEstablished {
      eventId
      occurredAt
    }
  }
`

type SessionNotice = { eventId: string; occurredAt: string }
const maxNoticeAge = 2 * 60 * 1000
const futureSkew = 30 * 1000

export function rememberSessionNotice(
  seen: Map<string, number>,
  notice: SessionNotice | undefined,
  now = Date.now()
): boolean {
  if (!notice?.eventId || notice.eventId.length > 128) return false
  const occurredAt = Date.parse(notice.occurredAt)
  if (
    !Number.isFinite(occurredAt) ||
    occurredAt < now - maxNoticeAge ||
    occurredAt > now + futureSkew
  )
    return false
  seen.forEach((expires, eventId) => {
    if (expires < now) seen.delete(eventId)
  })
  if (seen.has(notice.eventId)) return false
  if (seen.size >= 128) return false
  seen.set(notice.eventId, occurredAt + maxNoticeAge)
  return true
}

function AuthenticatedSessionLoginNotice() {
  const { data } = useSubscription<{
    otherSessionEstablished: SessionNotice
  }>(OTHER_SESSION_ESTABLISHED, {
    onError: () => {
      console.warn('Live session security notices are unavailable.')
    }
  })
  const seen = useRef(new Map<string, number>())

  useEffect(() => {
    if (!rememberSessionNotice(seen.current, data?.otherSessionEstablished))
      return
    toast.warn(getTranslate('A new login to your account was detected.'), {
      autoClose: 10000,
      closeOnClick: false
    })
  }, [data])

  return null
}

export default function SessionLoginNotice() {
  const credentials = useReactiveVar(credentialsVar)
  // credentialsVar is filled only from the `me` query, which succeeds only after
  // login (and 2FA) completes; `me` carries no `nextStep`, so gate on its token.
  if (!credentials?.accessToken) return null
  return <AuthenticatedSessionLoginNotice key={credentials.email} />
}
